import { useEffect } from 'react'
import { Link, Navigate, NavLink, useParams } from 'react-router-dom'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Navigation } from 'swiper'
import Layout from '../components/Layout'
import CodeBlock from '../components/CodeBlock'
import { outline, solid } from '../components/ContactButtons'
import { projects } from '../projects'
import { techURLs } from '../techs'
import { site } from '../site'
import NotFound from './NotFound'

const BASE_URL = process.env.PUBLIC_URL

function Section({ title, children }) {
  return (
    <section>
      <h2 className='text-lg font-bold'>{title}</h2>
      <hr className='mt-1' />
      <div className='pt-3 text-gray-700 text-sm leading-relaxed'>{children}</div>
    </section>
  )
}

function Project() {
  const { id } = useParams()
  const project = projects.find((p) => p.slug === id)
  const renamed = projects.find((p) => p.legacySlugs?.includes(id))

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [id])

  if (renamed) return <Navigate to={`/${renamed.slug}`} replace />
  if (!project) return <NotFound />

  const {
    slug, title, tagline, kind, role, timeline, stack, links,
    captions, overview, highlights, team, decisions, tradeoffs, related,
  } = project

  return (
    <Layout title={title}>
      <div className='text-xs uppercase tracking-wide text-gray-400'>{kind}</div>
      <h1 className='text-3xl lg:text-4xl font-semibold mt-1'>{title}</h1>
      <p className='mt-2 text-gray-600'>{tagline}</p>

      <dl className='mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm border-y py-4'>
        <div>
          <dt className='text-gray-400'>Role</dt>
          <dd className='mt-1'>{role}</dd>
        </div>
        <div>
          <dt className='text-gray-400'>Timeline</dt>
          <dd className='mt-1'>{timeline}</dd>
        </div>
        <div>
          <dt className='text-gray-400'>Links</dt>
          <dd className='mt-1 flex flex-wrap gap-x-4'>
            {links.website && (
              <a className='link' href={links.website} target='_blank' rel='noreferrer noopener'>
                Live site
              </a>
            )}
            {links.github ? (
              <a className='link' href={links.github} target='_blank' rel='noreferrer noopener'>
                GitHub
              </a>
            ) : (
              <span className='text-gray-500'>Private repo; I can walk through the code on request</span>
            )}
          </dd>
        </div>
      </dl>

      <div className='w-full mt-8'>
        <Swiper
          slidesPerView={1}
          spaceBetween={30}
          loop={true}
          pagination={{ type: 'progressbar' }}
          navigation={true}
          modules={[Pagination, Navigation]}
          className='mySwiper'
        >
          {captions.map((caption, index) => (
            <SwiperSlide key={index}>
              <figure>
                <img
                  src={`${BASE_URL}/images/${slug}/${slug}${index}.webp`}
                  alt={caption}
                  draggable={false}
                  className='w-full'
                />
                <figcaption className='text-center text-sm pt-4 text-gray-600 italic'>
                  {caption}
                </figcaption>
              </figure>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div className='space-y-12 mt-10'>
        <Section title='Overview'>
          <div className='space-y-3'>
            {overview.map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </div>
        </Section>

        <Section title={kind === 'Team project' ? 'What I built' : 'Highlights'}>
          <ul className='list-disc pl-5 space-y-2'>
            {highlights.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </Section>

        {team && (
          <Section title='Team and process'>
            <ul className='list-disc pl-5 space-y-2'>
              {team.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </Section>
        )}

        <Section title='Engineering decisions'>
          <ol className='list-decimal marker:font-semibold pl-5 space-y-8'>
            {decisions.map((d) => (
              <li key={d.title} className='space-y-2'>
                <b>{d.title}</b>
                <p>{d.problem}</p>
                <p>{d.approach}</p>
                {d.code && (
                  <div>
                    <div className='text-xs text-gray-500 mt-3'>
                      <code>{d.file}</code> (excerpt)
                    </div>
                    <CodeBlock code={d.code} language={d.language} />
                  </div>
                )}
              </li>
            ))}
          </ol>
        </Section>

        <Section title="Trade-offs and what I'd change">
          <ul className='list-disc pl-5 space-y-2'>
            {tradeoffs.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </Section>

        {related && (
          <Section title='Related posts'>
            <ul className='list-disc pl-5 space-y-1'>
              {related.map((post) => (
                <li key={post.to}>
                  <Link className='link' to={post.to}>{post.label}</Link>
                </li>
              ))}
            </ul>
          </Section>
        )}

        <section>
          <h2 className='text-lg font-bold mb-1'>Tech stack</h2>
          <div className='flex flex-wrap'>
            {stack.map((key) => (
              <img key={key} src={techURLs[key]} alt={key} className='p-0.5' />
            ))}
          </div>
        </section>

        <div className='flex flex-wrap gap-2'>
          <NavLink to='/' className={outline}>
            view all projects
          </NavLink>
          <a className={solid} href={`mailto:${site.email}`}>
            message me
          </a>
        </div>
      </div>
    </Layout>
  )
}

export default Project
